// Interface pro detail custom eventu
import Loader from './Loader';
import CustomCartSelect from './CustomCartSelect';

interface SelectChangeDetail {
    value: string;
    text: string;
    option: HTMLElement;
}

// Interface pro data produktu
interface ProductData {
    selectedVariant: string | null;
    selectedQuantity: string | null;
    pricePerUnit: number;
    totalPrice: number;
    discount: number;
}

export default class CartForm {
    private static productData: ProductData = {
        selectedVariant: null,
        selectedQuantity: null,
        pricePerUnit: 0,
        totalPrice: 0,
        discount: 0,
    };

    static init(): void {
        const cartFormContainer = document.querySelector('[data-form-add-to-basket-container]');
        if (!cartFormContainer) return;

        // Parse initial price from the DOM
        this.parseInitialPrice();

        // Inicializace výběru variant
        this.initVariantSelection();

        // Inicializace výběru počtu kusů
        this.initQuantitySelection();

        // Inicializace formuláře
        this.initCartForm();
    }

    /**
     * Parse Czech formatted price string to number
     * Handles formats like "1 047,50 Kč/ks" or "1 047 Kč/ks"
     */
    private static parseCzechPrice(priceString: string): number {
        if (!priceString) return 0;

        // Remove currency and unit text (Kč/ks, Kč, etc.)
        let cleanPrice = priceString.replace(/\s*(Kč|ks).*$/i, '').trim();

        // Replace spaces (thousand separators) with nothing
        cleanPrice = cleanPrice.replace(/\s/g, '');

        // Replace comma (decimal separator) with dot
        cleanPrice = cleanPrice.replace(',', '.');

        const price = parseFloat(cleanPrice);
        return isNaN(price) ? 0 : price;
    }

    /**
     * Parse price string that might be formatted
     * Handles both plain numbers and formatted strings
     */
    private static parsePrice(priceString: string): number {
        if (!priceString) return 0;

        // If it's already a valid number, return it
        const directParse = parseFloat(priceString);
        if (!isNaN(directParse)) return directParse;

        // Otherwise, handle formatted price (comma as thousand separator)
        // Remove commas and parse
        const cleanPrice = priceString.replace(/,/g, '');
        const price = parseFloat(cleanPrice);
        return isNaN(price) ? 0 : price;
    }

    /**
     * Parse initial price from the DOM element
     */
    private static parseInitialPrice(): void {
        const unitPriceElement = document.querySelector('[data-unit-price]') as HTMLElement;
        if (!unitPriceElement) return;

        // Try to get price from data attribute first
        const dataPrice = unitPriceElement.getAttribute('data-unit-price');
        if (dataPrice && !isNaN(parseFloat(dataPrice))) {
            this.productData.pricePerUnit = parseFloat(dataPrice);
        } else {
            // Otherwise parse from text content
            const priceText = unitPriceElement.textContent || '';
            this.productData.pricePerUnit = this.parseCzechPrice(priceText);
        }
    }

    /**
     * Inicializace výběru variant (barev)
     */
    private static initVariantSelection(): void {
        const variantRadios = document.querySelectorAll('input[name="variant"]') as NodeListOf<HTMLInputElement>;
        const variantSelect = document.querySelector('select[data-variant-select]') as HTMLSelectElement;

        variantRadios.forEach((radio) => {
            radio.addEventListener('change', (event) => {
                const target = event.target as HTMLInputElement;
                if (target.checked) {
                    this.handleVariantChange(target);
                    this.handleChangeHeaderImage(target);
                }
            });
        });

        if (variantSelect) {
            variantSelect.addEventListener('change', (event) => {
                const target = event.target as HTMLSelectElement;
                const selectedOption = target.selectedOptions[0] as HTMLOptionElement;
                if (selectedOption) {
                    this.handleVariantChange(selectedOption);
                }
            });

            // Nastavení výchozí varianty pro select
            const selectedOption = variantSelect.selectedOptions[0] as HTMLOptionElement;
            if (selectedOption) {
                this.handleVariantChange(selectedOption);
            }
        }

        // Nastavení výchozí varianty pro radio buttons
        const checkedVariant = document.querySelector('input[name="variant"]:checked') as HTMLInputElement;
        if (checkedVariant) {
            this.handleVariantChange(checkedVariant);
        }
    }

    /**
     * Inicializace výběru počtu kusů
     */
    private static initQuantitySelection(): void {
        const quantityValueInput = document.querySelector('input[data-select-value]') as HTMLInputElement | null;

        if (quantityValueInput) {
            document.addEventListener('selectChange', (event: Event) => {
                const customEvent = event as CustomEvent<SelectChangeDetail>;
                this.handleQuantityChange(customEvent.detail);
            });

            document.addEventListener('pricingSelectChange', (event: Event) => {
                const customEvent = event as CustomEvent<any>;
                this.handlePricingSelectChange(customEvent.detail);
            });

            this.selectFirstQuantityOption();
        }
    }

    /**
     * Výběr první možnosti množství
     */
    private static selectFirstQuantityOption(): void {
        const firstOption = document.querySelector('[data-select-quantity] .option') as HTMLElement;
        if (!firstOption) {
            // If no options available, check if we need to show MOQ pricing
            this.checkMOQPricing();
            return;
        }
        firstOption.click();
    }

    /**
     * Check and set MOQ pricing if needed
     */
    private static checkMOQPricing(): void {
        const pricingDataScript = document.querySelector('.quantity-pricing-data');
        if (!pricingDataScript || !pricingDataScript.textContent) return;

        let quantityPricingData: any;
        try {
            quantityPricingData = JSON.parse(pricingDataScript.textContent);
        } catch (e) {
            return;
        }

        const minQuantity = quantityPricingData.minQuantity;
        if (!minQuantity || minQuantity <= 1) return;

        // Check if MOQ has a defined price tier
        const pricing = this.calculatePricingForQuantity(minQuantity);
        if (pricing && pricing.pricePerUnit === 0) {
            // MOQ doesn't have a price tier, show "Na vyžádání"
            this.productData.selectedQuantity = minQuantity.toString();
            this.productData.pricePerUnit = 0;
            this.productData.totalPrice = 0;
            this.productData.discount = 0;
            this.updatePriceDisplay();
            this.updateFormData();
        }
    }

    /**
     * Inicializace formuláře košíku
     */
    private static initCartForm(): void {
        const cartForm = document.querySelector('[data-form-add-to-basket]') as HTMLFormElement;
        if (!cartForm) return;

        cartForm.addEventListener('submit', (event) => {
            // Aktualizace formulářových dat před odesláním
            this.updateFormData();

            // Validace - pokud selže, zabrání odeslání
            if (!this.validateForm()) {
                event.preventDefault();
                return;
            }

            // Pokud validace prošla, nechá Naja zpracovat odeslání automaticky
            Loader.show();
        });
    }

    /**
     * Zpracování změny varianty
     */
    private static handleVariantChange(element: HTMLInputElement | HTMLOptionElement): void {
        const variantId = element.value;

        this.updateVariantVisual(element);

        this.productData.selectedVariant = variantId;

        this.updateFormData();

        // Load variant pricing dynamically
        this.loadVariantPricing(variantId);
    }

    /**
     * Zpracování změny počtu kusů
     */
    private static handleQuantityChange(detail: SelectChangeDetail): void {
        const { value, option } = detail;

        if (value === 'other') {
            console.log('Vybráno "Jiné množství"');
            return;
        }

        // Parse price, handling potential formatting
        const pricePerUnitStr = option.dataset.pricePerUnit || '0';
        const pricePerUnit = this.parsePrice(pricePerUnitStr);
        const discount = parseFloat(option.dataset.discount || '0');
        const totalPrice = parseFloat(option.dataset.totalPrice || '0');

        // Uložení do produktových dat
        this.productData.selectedQuantity = value;
        this.productData.pricePerUnit = pricePerUnit;
        this.productData.totalPrice = totalPrice;
        this.productData.discount = discount;

        // Aktualizace zobrazení ceny
        this.updatePriceDisplay();

        // Aktualizace formuláře
        this.updateFormData();
    }

    /**
     * Zpracování změny počtu kusů z pricing selectu
     */
    private static handlePricingSelectChange(detail: any): void {
        // Standardní možnost
        let quantity = detail.value;
        let pricePerUnit = 0;
        let discount = 0;
        let totalPrice = 0;

        if (detail.isCustom && detail.quantity) {
            // Vlastní množství - vypočítat ceny z pricing dat
            quantity = detail.quantity.toString();
            this.productData.selectedQuantity = detail.quantity.toString();

            const pricing = this.calculatePricingForQuantity(detail.quantity);
            if (pricing) {
                this.productData.pricePerUnit = pricing.pricePerUnit;
                this.productData.totalPrice = pricing.totalPrice;
                this.productData.discount = pricing.discount;
            } else {
                this.productData.pricePerUnit = 0;
                this.productData.totalPrice = 0;
                this.productData.discount = 0;
            }
        } else if (detail.option) {
            // Standardní možnost
            quantity = detail.value;
            const pricePerUnitStr = detail.option.dataset.pricePerUnit || '0';
            pricePerUnit = this.parsePrice(pricePerUnitStr);
            discount = parseFloat(detail.option.dataset.discount || '0');
            totalPrice = parseFloat(detail.option.dataset.totalPrice || '0');

            this.productData.selectedQuantity = quantity;
        }

        // For custom quantities, selectedQuantity is already set above
        // For standard options, selectedQuantity was set in the else block
        if (!detail.isCustom) {
            this.productData.pricePerUnit = pricePerUnit;
            this.productData.totalPrice = totalPrice;
            this.productData.discount = discount;
        }

        // Aktualizace zobrazení ceny
        this.updatePriceDisplay();

        // Aktualizace formuláře
        this.updateFormData();
    }

    /**
     * Aktualizace vizuálního výběru varianty
     */
    private static updateVariantVisual(element: HTMLInputElement | HTMLOptionElement): void {
        // Only update visual for radio buttons (color picker), not for select dropdowns
        if (element instanceof HTMLInputElement && element.type === 'radio') {
            // Odebrání aktivní třídy ze všech položek
            const allItems = document.querySelectorAll('.detail-header-color__item');
            allItems.forEach((item) => item.classList.remove('is-active'));

            // Přidání aktivní třídy k vybrané položce
            const selectedItem = element.parentElement?.querySelector('.detail-header-color__item');
            selectedItem?.classList.add('is-active');
        }
        // For select options, no visual update needed - browser handles select appearance
    }

    /**
     * Aktualizace zobrazení ceny
     */
    private static updatePriceDisplay(): void {
        const unitPriceElement = document.querySelector('[data-unit-price]') as HTMLSpanElement;
        const totalPriceElement = document.querySelector('[data-quantity-total-price]');
        const originalPriceElement = document.querySelector('[data-original-price]') as HTMLSpanElement;
        const originalPriceContainer = originalPriceElement?.closest('[data-original-price-container]') as HTMLElement;

        const unitPriceSuffix = document.querySelector('[data-unit-price-suffix]') as HTMLElement | null;
        if (unitPriceElement) {
            if (this.productData.pricePerUnit > 0) {
                // Set the text content with formatted price for display
                unitPriceElement.textContent = `${this.productData.pricePerUnit.toLocaleString('cs-CZ')} Kč/ks`;
                // Set the data attribute with unformatted numeric value for calculations
                unitPriceElement.setAttribute('data-unit-price', this.productData.pricePerUnit.toString());
                if (unitPriceSuffix) unitPriceSuffix.style.display = '';
            } else {
                // Handle "Na vyžádání" case
                unitPriceElement.textContent = 'Na vyžádání';
                unitPriceElement.setAttribute('data-unit-price', '0');
                if (unitPriceSuffix) unitPriceSuffix.style.display = 'none';
            }
        }

        if (totalPriceElement) {
            if (this.productData.totalPrice > 0) {
                totalPriceElement.textContent = `${this.productData.totalPrice.toLocaleString('cs-CZ')} Kč bez DPH`;
            } else {
                totalPriceElement.textContent = '';
            }
        }

        if (originalPriceContainer) {
            if (this.productData.discount > 0 && this.productData.pricePerUnit > 0) {
                const originalPrice = this.productData.pricePerUnit / (1 - this.productData.discount / 100);
                originalPriceElement.textContent = `${originalPrice.toLocaleString('cs-CZ')} Kč/ks`;
                originalPriceContainer.classList.remove('none');
                unitPriceElement.classList.add('c-red-crab');
            } else {
                originalPriceContainer.classList.add('none');
                unitPriceElement.classList.remove('c-red-crab');
            }
        }
    }

    /**
     * Aktualizace dat formuláře
     */
    private static updateFormData(): void {
        const cartForm = document.querySelector('[data-form-add-to-basket]') as HTMLFormElement;
        if (!cartForm) return;

        // Najdeme nebo vytvoříme skrytá pole pro variantu a množství
        let variantInput = cartForm.querySelector('[name="variant"]') as HTMLInputElement;
        if (!variantInput) {
            variantInput = document.createElement('input');
            variantInput.type = 'hidden';
            variantInput.name = 'variant';
            cartForm.appendChild(variantInput);
        }

        const quantityInput = cartForm.querySelector('[name="quantity"]') as HTMLInputElement;

        // Aktualizace hodnot
        if (this.productData.selectedVariant) {
            variantInput.value = this.productData.selectedVariant;
        }

        if (this.productData.selectedQuantity) {
            quantityInput.value = this.productData.selectedQuantity;
        }
    }

    /**
     * Výpočet ceny pro vlastní množství na základě cenových pásem
     */
    private static calculatePricingForQuantity(
        quantity: number,
    ): { pricePerUnit: number; discount: number; totalPrice: number } | null {
        const pricingDataScript = document.querySelector('.quantity-pricing-data');
        if (!pricingDataScript || !pricingDataScript.textContent || quantity <= 0) {
            return null;
        }

        let quantityPricingData: any;
        try {
            quantityPricingData = JSON.parse(pricingDataScript.textContent);
        } catch (e) {
            console.warn('Failed to parse quantity pricing data:', e);
            return null;
        }

        const quantityPrices = quantityPricingData.quantityPrices;
        const basePrice = quantityPricingData.basePrice;

        // Find lowest tier
        let lowestTierQuantity = Infinity;
        for (const tier of quantityPrices) {
            if (tier.quantityFrom < lowestTierQuantity) {
                lowestTierQuantity = tier.quantityFrom;
            }
        }

        // If quantity is below lowest tier, price is "Na vyžádání" (0)
        if (quantity < lowestTierQuantity) {
            return {
                pricePerUnit: 0,
                discount: 0,
                totalPrice: 0,
            };
        }

        // Check if quantity matches any defined price tier
        let applicablePrice: number | null = null;
        let foundTier = false;

        for (let i = quantityPrices.length - 1; i >= 0; i--) {
            const tier = quantityPrices[i];
            if (quantity >= tier.quantityFrom) {
                // Check if this tier covers the quantity (if quantityTo exists)
                if (!tier.quantityTo || quantity <= tier.quantityTo) {
                    applicablePrice = tier.price;
                    foundTier = true;
                    break;
                }
            }
        }

        // If no tier found for this quantity, it means price is "Na vyžádání" (0)
        if (!foundTier) {
            return {
                pricePerUnit: 0,
                discount: 0,
                totalPrice: 0,
            };
        }

        // Calculate discount percentage
        const discount = basePrice > 0 && applicablePrice !== null ? (1 - applicablePrice / basePrice) * 100 : 0;

        // Calculate total price
        const totalPrice = applicablePrice !== null ? applicablePrice * quantity : 0;

        return {
            pricePerUnit: applicablePrice || 0,
            discount: Math.max(0, discount),
            totalPrice: totalPrice,
        };
    }

    /**
     * Validace formuláře
     */
    private static validateForm(): boolean {
        const checkedVariant = document.querySelector('input[name="variant"]:checked') as HTMLInputElement;
        if (!this.productData.selectedVariant && checkedVariant) {
            alert('Prosím vyberte variantu produktu.');
            return false;
        }

        if (!this.productData.selectedQuantity || this.productData.selectedQuantity === '0') {
            alert('Prosím vyberte počet kusů.');
            return false;
        }

        return true;
    }

    private static handleChangeHeaderImage(element: HTMLInputElement | HTMLOptionElement): void {
        const imageUrl = element.dataset.variantImage;
        
        // Don't change image if variant has no image (data-variant-image attribute not present)
        if (!imageUrl || !element.hasAttribute('data-variant-image')) return;

        const slider = document.querySelector('.js-detail-header-slider-init');
        if (!slider) return;

        const sliderItems = slider.querySelectorAll(
            '.detail-header-slider__control-item:not(.detail-header-slider__control-item--trigger)',
        ) as NodeListOf<HTMLElement>;
        
        sliderItems.forEach((item) => {
            if (item.dataset.slideVariant === element.value) {
                item.click();
            }
        });
    }

    /**
     * Load variant pricing via AJAX and update quantity options
     */
    private static async loadVariantPricing(variantId: string): Promise<void> {
        const container = document.querySelector('[data-form-add-to-basket-container]');
        if (!container) return;

        const urlTemplate = (container as HTMLElement).dataset.variantPricingUrl;
        if (!urlTemplate) return;

        // Replace placeholder with actual variant ID
        const url = urlTemplate.replace('VARIANT_ID', variantId);

        // Store current quantity selection before loading new variant
        const currentQuantity = this.productData.selectedQuantity;

        try {
            Loader.show();
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.error) {
                console.error('Error loading variant pricing:', data.error);
                return;
            }

            // Update quantity options with new pricing
            this.updateQuantityOptions(data);
            
            // Update price display with new variant price
            this.updateVariantPriceDisplay(data.variantPrice);

            // Restore quantity selection if it was set
            if (currentQuantity) {
                this.restoreQuantitySelection(currentQuantity);
            }

        } catch (error) {
            console.error('Failed to load variant pricing:', error);
        } finally {
            Loader.hide();
        }
    }

    /**
     * Update quantity options with new pricing data
     */
    private static updateQuantityOptions(pricingData: any): void {
        const optionsContainer = document.querySelector('.select-options__inner');
        if (!optionsContainer) return;

        // Store current quantity before clearing options
        const currentQuantity = this.productData.selectedQuantity;

        // Clear existing options except custom input
        const existingOptions = optionsContainer.querySelectorAll('.option:not(.custom-input-option)');
        existingOptions.forEach(option => option.remove());

        const basePrice = pricingData.basePrice;

        // Create new options from variant pricing
        pricingData.quantityPrices.forEach((quantityPrice: any) => {
            const quantity = quantityPrice.quantity;
            const discount = quantityPrice.discount;
            const totalPrice = quantityPrice.totalPrice;

            const optionElement = document.createElement('div');
            optionElement.className = 'option';
            optionElement.setAttribute('data-value', quantity.toString());
            optionElement.setAttribute('data-text', `${quantity} kusů`);
            optionElement.setAttribute('data-price-per-unit', quantityPrice.pricePerUnit.toString());
            optionElement.setAttribute('data-discount', discount.toString());
            optionElement.setAttribute('data-total-price', totalPrice.toString());

            optionElement.innerHTML = `
                <div class="option-left">
                    <div class="option-quantity">${quantity} <span>ks</span></div>
                    <div class="option-price">${this.formatPrice(quantityPrice.pricePerUnit)} Kč/ks</div>
                </div>
                <div class="option-right">
                    <div class="option-discount">
                        ${discount > 0 ? `Sleva ${Math.round(discount)} %` : ''}
                    </div>
                    <div class="option-total">${this.formatPrice(totalPrice, 0)} Kč</div>
                </div>
            `;

            // Insert before custom input option
            const customInputOption = optionsContainer.querySelector('.custom-input-option');
            if (customInputOption) {
                optionsContainer.insertBefore(optionElement, customInputOption);
            } else {
                optionsContainer.appendChild(optionElement);
            }
        });

        // Update pricing data script for custom quantity calculation
        this.updatePricingDataScript(pricingData);

        // Only reset selection if we don't have a quantity to restore
        if (!currentQuantity) {
            this.resetQuantitySelection();
        }

        // Reinitialize custom select component to attach event handlers to new options
        CustomCartSelect.init();
    }

    /**
     * Update pricing data script for custom quantity calculations
     */
    private static updatePricingDataScript(pricingData: any): void {
        const pricingScript = document.querySelector('.quantity-pricing-data');
        if (!pricingScript) return;

        const newData = {
            quantityPrices: pricingData.quantityPrices.map((qp: any) => ({
                quantityFrom: qp.quantityFrom,
                quantityTo: qp.quantityTo || null,
                price: qp.pricePerUnit
            })),
            basePrice: pricingData.basePrice,
            minQuantity: pricingData.minQuantity
        };

        pricingScript.textContent = JSON.stringify(newData);
    }

    /**
     * Update variant price display
     */
    private static updateVariantPriceDisplay(variantPrice: number): void {
        const unitPriceElement = document.querySelector('[data-unit-price]');
        const originalPriceElement = document.querySelector('[data-original-price]');
        
        if (unitPriceElement) {
            unitPriceElement.innerHTML = `<strong class="base-text base-text--xl">${this.formatPrice(variantPrice)} Kč/ks</strong>`;
        }
        const unitPriceSuffix = document.querySelector('[data-unit-price-suffix]') as HTMLElement | null;
        if (unitPriceSuffix) {
            unitPriceSuffix.style.display = variantPrice > 0 ? '' : 'none';
        }

        if (originalPriceElement) {
            originalPriceElement.textContent = this.formatPrice(variantPrice);
        }
    }

    /**
     * Reset quantity selection
     */
    private static resetQuantitySelection(): void {
        this.productData.selectedQuantity = null;
        this.productData.pricePerUnit = 0;
        this.productData.totalPrice = 0;
        this.productData.discount = 0;

        // Reset select display
        const selectedValue = document.querySelector('.selected-value');
        if (selectedValue) {
            selectedValue.textContent = 'Zvolte počet kusů';
        }

        // Clear quantity input
        const quantityInput = document.querySelector('input[data-select-value]') as HTMLInputElement;
        if (quantityInput) {
            quantityInput.value = '0';
        }

        // Reset price display
        const quantityTotalPrice = document.querySelector('[data-quantity-total-price]');
        if (quantityTotalPrice) {
            quantityTotalPrice.textContent = '';
        }
    }

    /**
     * Format price with Czech formatting
     */
    private static formatPrice(price: number, decimals: number = 2): string {
        return price.toLocaleString('cs-CZ', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }

    /**
     * Restore quantity selection after variant change
     */
    private static restoreQuantitySelection(quantity: string): void {
        const quantityNumber = parseInt(quantity, 10);
        if (isNaN(quantityNumber) || quantityNumber <= 0) return;

        // Try to find a matching quantity option
        const option = document.querySelector(`.option[data-value="${quantity}"]:not(.custom-input-option)`) as HTMLElement;
        
        if (option) {
            // Standard quantity option exists - click it
            option.click();
        } else {
            // No standard option - use custom quantity
            const customInput = document.querySelector('.custom-quantity-input') as HTMLInputElement;
            if (customInput) {
                customInput.value = quantity;
                
                // Trigger input event to calculate pricing
                const inputEvent = new Event('input', { bubbles: true });
                customInput.dispatchEvent(inputEvent);
                
                // Trigger change event to update selection
                const changeEvent = new Event('change', { bubbles: true });
                customInput.dispatchEvent(changeEvent);
            }
        }
    }
}
